import { useCallback, useEffect, useState } from 'react';
import { patientApi, type PrescribedMedicine } from '../api';

/**
 * Medicines a doctor prescribed that are not being tracked yet.
 *
 * The Medicines page lists reminders, and marks the ones that came from a
 * prescription. What it could not show is the gap: a medicine a doctor
 * prescribed which never became a reminder simply did not appear, so the list
 * looked complete while a course was missing from it.
 *
 * `GET /pharmacy/prescribed-medicines` is the doctors' side of that. Anything
 * here without a matching reminder is offered with the button that creates
 * one, using the server's own from-prescription route so the dose and
 * schedule come from the prescription rather than being retyped.
 */
export default function PrescribedNotTracked({
  trackedNames,
  onTracked,
}: {
  /** Lower-cased names already on the reminder list. */
  trackedNames: Set<string>;
  onTracked: () => void;
}) {
  const [rows, setRows] = useState<PrescribedMedicine[]>([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setRows(await patientApi.pharmacyModule.prescribedMedicines() ?? []);
    } catch {
      // The reminder list is the page's job; this section just stays away.
      setRows([]);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const untracked = rows.filter(
    (r) => (r.status || '').toUpperCase() !== 'STOPPED'
      && !trackedNames.has((r.medicineName || '').trim().toLowerCase()),
  );

  if (untracked.length === 0) return null;

  const track = async (r: PrescribedMedicine) => {
    if (!r.prescriptionId) return;
    setBusy(r.id);
    setError('');
    try {
      await patientApi.createRemindersFromPrescription(r.prescriptionId);
      onTracked();
      await load();
    } catch (e: any) {
      setError(e?.body?.message || 'A reminder could not be created for that prescription.');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4">
      <p className="text-sm font-semibold text-amber-900">
        Prescribed, but not on your reminder list
      </p>
      <p className="text-xs text-amber-800 mt-1 leading-relaxed">
        Your doctor prescribed {untracked.length === 1 ? 'this' : 'these'} and nothing here is tracking{' '}
        {untracked.length === 1 ? 'it' : 'them'} yet.
      </p>

      {error && <p className="text-xs text-red-700 mt-2">{error}</p>}

      <div className="mt-3 space-y-2">
        {untracked.map((r) => (
          <div key={r.id} className="bg-white border border-amber-100 rounded-xl p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900">{r.medicineName}</p>
                <p className="text-[11px] text-gray-600 mt-0.5">
                  {[r.dose, r.frequency, r.duration].filter(Boolean).join(' · ')}
                </p>
                {r.instructions && (
                  <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">{r.instructions}</p>
                )}
                <p className="text-[10px] text-gray-400 mt-1">
                  {[r.doctorName, r.prescribedAt ? new Date(r.prescribedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : null]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
              {r.prescriptionId && (
                <button
                  onClick={() => void track(r)}
                  disabled={busy === r.id}
                  className="px-3 py-2 text-xs font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 shrink-0"
                >
                  {busy === r.id ? 'Adding…' : 'Track this'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
